import KudosStarIcon from '@assets/icons/KudosStarIcon'
import { useDialog } from '@globals/dialog/useDialogStore'
import { isKudosMobileShell } from '@globals/nativeBridge'
import { usePWAInstallationPromptStore } from '@globals/stores/usePWAInstallationPromptStore'
import { colors } from '@globals/themes/defaultTheme'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseButton from '@shared/components/button/CloseButton'
import { getAppStoreTypeFromUserAgent } from '@shared/utils/getAppStoreTypeFromUserAgent'
import { T } from '@transifex/react'
import { SyntheticEvent, useEffect, useState } from 'react'
import PWAInstallationModal from './PWAInstallationModal'

const KNG_IOS_APP_STORE = 'https://apps.apple.com/us/app/kudos-next-generation/id6771839589'
const KNG_ANDROID_PLAY_STORE = 'https://play.google.com/store/apps/details?id=com.kudos.mobileapp'

const dialogKey = 'pwaInstallationPrompt'

type BeforeInstallPromptEvent = Event & {
  prompt: () => void
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const cardStyle = {
  p: 2,
  boxShadow: 'none',
  backgroundColor: colors.blueUltraLight,
  borderRadius: 0,
}

const appStoreType = getAppStoreTypeFromUserAgent(
  typeof navigator === 'undefined' ? undefined : navigator.userAgent,
)

const getNativeAppStoreUrl = (): string | null => {
  if (appStoreType === 'ios') return KNG_IOS_APP_STORE
  if (appStoreType === 'android') return KNG_ANDROID_PLAY_STORE
  return null
}

const AppInstallationPrompt = () => {
  const [promptInstall, setPromptInstall] = useState<Event | null>(null)
  const { openDialog } = useDialog(dialogKey)
  const { dismissed, setDismissed } = usePWAInstallationPromptStore()

  const nativeAppStoreUrl = getNativeAppStoreUrl()

  useEffect(() => {
    // Only add event listener in non-store browser e.g. desktop or mobile that is not iOS or Android
    if (appStoreType !== null) return

    const handler = (e: Event) => {
      e.preventDefault()
      setPromptInstall(e)
    }
    window.addEventListener('beforeinstallprompt', handler)

    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  if (isKudosMobileShell() || dismissed) {
    return null
  }

  if (nativeAppStoreUrl) {
    return (
      <Card sx={cardStyle}>
        <Stack useFlexGap direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <KudosStarIcon />
          <Stack sx={{ alignItems: 'flex-start' }}>
            <Button
              variant="text"
              component="a"
              href={nativeAppStoreUrl}
              id="btnMobileAppInstall"
              rel="noopener noreferrer">
              <T _str="Download the Kudos app" />
            </Button>
            <Typography variant="caption">
              <T _str="For the best mobile experience" />
            </Typography>
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <CloseButton
            id="btnCloseAppInstallationPrompt"
            ariaLabel="Close mobile app install prompt"
            onClose={() => setDismissed(true)}
            sx={{ position: 'unset' }}
          />
        </Stack>
      </Card>
    )
  }

  const onClick = (evt: SyntheticEvent<HTMLElement, Event>) => {
    evt.preventDefault()

    if (!promptInstall) {
      return openDialog()
    }
    ;(promptInstall as BeforeInstallPromptEvent).prompt()
  }

  return (
    <>
      <Card sx={cardStyle}>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: 'center',
          }}>
          <KudosStarIcon />
          <Stack>
            <Button variant="text" id="btnPWAInstall" onClick={onClick}>
              <T _str="Add Kudos to your home screen" />
            </Button>
            <Typography variant="caption">
              <T _str="For the best mobile experience" />
            </Typography>
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <CloseButton
            id="btnClosePWAInstallationPrompt"
            ariaLabel="Close PWA installation prompt"
            onClose={() => setDismissed(true)}
            sx={{ position: 'unset' }}
          />
        </Stack>
      </Card>
      <PWAInstallationModal
        dialogKey={dialogKey}
        listItem1={<T _str="You can add Kudos to your home screen for quicker access" />}
        listItem2={
          <span>
            <T
              _str="Open your browser menu and select {boldAddToHomeScreen}"
              boldAddToHomeScreen={
                <b>
                  <span>
                    <T _str="Add to home screen" />
                  </span>
                </b>
              }
            />
          </span>
        }
      />
    </>
  )
}

export default AppInstallationPrompt
